import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Boton } from "@iavmx/ui";
import {
  abrirSesionDesdeLiga,
  leerLiga,
  ligaVencida,
  limpiarLigaDeUrl,
  type Liga,
} from "@iavmx/auth/navegador";
import { MarcoAcceso, Mensaje } from "../componentes";
import { useSesion } from "../sesion";
import type { EstadoAcceso } from "../tipos";
import { leer } from "../api";

// La liga se lee al cargar el módulo, antes de que supabase-js (detectSessionInUrl) la toque.
const ligaInicial: Liga = typeof window !== "undefined" ? leerLiga(window.location.href) : { clase: "nada" };

type Pantalla =
  | { tipo: "procesando" }
  | { tipo: "vencida" }
  | { tipo: "otro_navegador" }
  | { tipo: "liga_invalida"; detalle: string }
  | { tipo: "fuera_de_dominio"; correo: string }
  | { tipo: "sin_invitacion"; correo: string }
  | { tipo: "desactivada"; correo: string; desde: string | null }
  | { tipo: "listo" };

/** ¿Este navegador pidió la liga? supabase-js guarda el verificador PKCE junto a la sesión. */
function hayVerificadorPkce(): boolean {
  try {
    return Object.keys(localStorage).some((k) => k.startsWith("sb-") && k.endsWith("-code-verifier"));
  } catch {
    return false;
  }
}

/**
 * /auth/confirmar (y su alias /entrar/callback): aquí aterrizan las ligas.
 *  ?token_hash=…&type=invite|magiclink|recovery|email_change   plantillas de correo → verifyOtp
 *  ?code=…                                                     PKCE (Google o liga pedida aquí) → exchangeCodeForSession
 *  #access_token=…                                             flujo implícito → setSession
 *  #error_code=otp_expired                                     Supabase ya la rechazó
 * Con la sesión abierta pregunta a /api/acceso por qué sí o por qué no puede pasar, y lo explica en español.
 */
export function Callback() {
  const { cliente, cargando, correo, estadoYo, recargarYo, cerrar, api } = useSesion();
  const navegar = useNavigate();
  const [pantalla, setPantalla] = React.useState<Pantalla>({ tipo: "procesando" });
  const procesado = React.useRef(false);

  React.useEffect(() => {
    if (cargando || !cliente || procesado.current) return;
    procesado.current = true;
    (async () => {
      const liga = ligaInicial;
      if (liga.clase === "error") {
        limpiarLigaDeUrl();
        if (ligaVencida(liga.codigo, liga.descripcion)) setPantalla({ tipo: "vencida" });
        else setPantalla({ tipo: "liga_invalida", detalle: liga.descripcion ?? liga.codigo ?? "" });
        return;
      }
      if (liga.clase !== "nada") {
        try {
          await abrirSesionDesdeLiga(cliente, liga);
        } catch (e) {
          // Con PKCE, supabase-js pudo haber canjeado el código él solo al iniciar; si ya hay sesión, seguimos.
          const { data } = await cliente.auth.getSession();
          if (!data.session) {
            limpiarLigaDeUrl();
            const msg = (e as Error).message ?? "";
            if (liga.clase === "code" && !hayVerificadorPkce()) setPantalla({ tipo: "otro_navegador" });
            else if (ligaVencida((e as { code?: string }).code ?? null, msg)) setPantalla({ tipo: "vencida" });
            else setPantalla({ tipo: "liga_invalida", detalle: msg });
            return;
          }
        }
        limpiarLigaDeUrl();
      }
      const acceso = await leer<{ estado: EstadoAcceso; correo: string | null; desde?: string | null }>(
        await api.api.acceso.$get(),
      );
      switch (acceso.estado) {
        case "sin_sesion":
          setPantalla(
            liga.clase === "nada"
              ? { tipo: "liga_invalida", detalle: "Esta página se abre desde la liga de tu correo." }
              : { tipo: "liga_invalida", detalle: "No se abrió ninguna sesión." },
          );
          return;
        case "fuera_de_dominio":
          setPantalla({ tipo: "fuera_de_dominio", correo: acceso.correo ?? "" });
          return;
        case "sin_invitacion":
          setPantalla({ tipo: "sin_invitacion", correo: acceso.correo ?? "" });
          return;
        case "desactivada":
          setPantalla({ tipo: "desactivada", correo: acceso.correo ?? "", desde: acceso.desde ?? null });
          return;
        default:
          setPantalla({ tipo: "listo" });
          await recargarYo();
      }
    })();
  }, [cargando, cliente, api, recargarYo]);

  React.useEffect(() => {
    if (pantalla.tipo === "listo" && correo && estadoYo === "ok") void navegar({ to: "/", replace: true });
  }, [pantalla, correo, estadoYo, navegar]);

  async function otraCuenta() {
    await cerrar();
    void navegar({ to: "/entrar" });
  }

  const titulo = (t: string) => <h1 className="font-titulo text-3xl font-bold leading-tight">{t}</h1>;

  if (!cargando && !cliente)
    return (
      <MarcoAcceso>
        <Mensaje tono="info">El acceso no está configurado en este entorno.</Mensaje>
      </MarcoAcceso>
    );

  return (
    <MarcoAcceso>
      <div className="space-y-5">
        {pantalla.tipo === "procesando" || pantalla.tipo === "listo" ? (
          <>
            {titulo("Abriendo tu sesión")}
            <p className="font-ui text-sm text-tinta-tenue" aria-live="polite">
              Un momento…
            </p>
          </>
        ) : pantalla.tipo === "vencida" ? (
          <>
            {titulo("Esta invitación ya venció")}
            <p className="font-ui text-sm text-tinta-2">
              Las ligas del correo sirven una sola vez y por tiempo limitado.
            </p>
            <Mensaje tono="error" titulo="La liga venció o ya se usó.">
              Pide otra con tu correo de Vanguardia; te llega en el momento. Si ya entraste antes, usa tu cuenta de Google.
            </Mensaje>
            <Boton variante="acento" className="h-11 w-full text-tinta" asChild>
              <Link to="/entrar/invitacion">Pedir otra liga</Link>
            </Boton>
            <Boton variante="contorno" className="h-11 w-full" asChild>
              <Link to="/entrar">Entrar con mi cuenta de Google</Link>
            </Boton>
          </>
        ) : pantalla.tipo === "otro_navegador" ? (
          <>
            {titulo("Abre la liga donde la pediste")}
            <p className="font-ui text-sm text-tinta-2">
              Esta liga solo abre en el navegador desde el que se pidió; aquí no encontramos ese rastro.
            </p>
            <Mensaje tono="info">
              Vuelve a pedir la liga desde este navegador y ábrela aquí mismo, o entra con tu cuenta de Google.
            </Mensaje>
            <Boton variante="acento" className="h-11 w-full text-tinta" asChild>
              <Link to="/entrar/invitacion">Pedir la liga desde aquí</Link>
            </Boton>
            <Boton variante="contorno" className="h-11 w-full" asChild>
              <Link to="/entrar">Entrar con mi cuenta de Google</Link>
            </Boton>
          </>
        ) : pantalla.tipo === "liga_invalida" ? (
          <>
            {titulo("No pudimos abrir la sesión")}
            <Mensaje tono="error" titulo="Esta liga no sirve.">
              {pantalla.detalle || "Vuelve a pedir una desde la entrada."}
            </Mensaje>
            <Boton variante="acento" className="h-11 w-full text-tinta" asChild>
              <Link to="/entrar/invitacion">Pedir otra liga</Link>
            </Boton>
            <Boton variante="liga" asChild>
              <Link to="/entrar">Volver a la entrada</Link>
            </Boton>
          </>
        ) : pantalla.tipo === "fuera_de_dominio" ? (
          <>
            {titulo("Esa cuenta no es de Vanguardia")}
            <p className="font-ui text-sm text-tinta-2">
              Entraste con <b>{pantalla.correo}</b>. iavmx solo acepta cuentas @vanguardia.com.mx.
            </p>
            <Mensaje tono="error" titulo="No se inició sesión.">
              Vuelve a intentar con la cuenta de Google con la que abres tu correo del trabajo.
            </Mensaje>
            <Boton variante="acento" className="h-11 w-full text-tinta" onClick={() => void otraCuenta()}>
              Intentar con mi cuenta de Vanguardia
            </Boton>
            <p className="font-ui text-xs text-tinta-tenue">
              Si no tienes cuenta de Vanguardia, pídela a Recursos Humanos. Las cuentas personales no entran.
            </p>
          </>
        ) : pantalla.tipo === "sin_invitacion" ? (
          <>
            {titulo("Todavía no tienes acceso")}
            <p className="font-ui text-sm text-tinta-2">
              Entraste con <b>{pantalla.correo}</b>. Es una cuenta de Vanguardia, pero nadie te ha invitado a iavmx.
            </p>
            <Mensaje tono="info">
              Pídele acceso a tu jefe de área o a quien administra iavmx en Estrategia Digital. Cuando te inviten, te llega un correo con una liga.
            </Mensaje>
            <Boton variante="contorno" className="h-11 w-full" onClick={() => void otraCuenta()}>
              Entrar con otra cuenta
            </Boton>
          </>
        ) : (
          <>
            {titulo("Tu cuenta está desactivada")}
            <p className="font-ui text-sm text-tinta-2">
              La cuenta <b>{pantalla.correo}</b> fue desactivada
              {pantalla.desde ? ` el ${new Date(pantalla.desde).toLocaleDateString("es-MX", { day: "numeric", month: "long" })}` : ""}. Tus solicitudes y cotizaciones se conservan.
            </p>
            <Mensaje tono="info">
              Si crees que es un error, habla con quien administra iavmx. Un administrador puede reactivarla.
            </Mensaje>
            <Boton variante="contorno" className="h-11 w-full" onClick={() => void otraCuenta()}>
              Entrar con otra cuenta
            </Boton>
          </>
        )}
      </div>
    </MarcoAcceso>
  );
}
